// StepConfigDrawer — right-side sheet that opens on flow node click.
// Header: step icon + name + step-type chip.
// Body: dispatcher by stepType. Rich forms cover common ETL + agentic steps;
// less common steps fall back to backend catalog schemas instead of a dead stub.
// Z-index: drawer uses --z-modal (inherited from SheetContent); popups inside
// must use tokens above --z-modal per CLAUDE.md z-index ladder.
// Mobile (<768px): renders as fullscreen bottom sheet instead of right-side panel.
import { useTranslations } from "use-intl";
import { X } from "lucide-react";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetBody,
  SheetFooter,
} from "@/components/ui/sheet/Sheet";
import { Button } from "@/components/ui/button";
import { getStepTypeConfig } from "../flow/step-constants";
import { FeatureDisabledCard, useFeatureEnabled } from "@/features/features";
import { ExtractConfigForm } from "./configs/extract-config-form";
import { LoadConfigForm } from "./configs/load-config-form";
import { CatalogStepConfigForm } from "./configs/catalog-step-config-form";
import { LlmCallConfigForm } from "./configs/llm-call-config-form";
import { AgentTaskConfigForm } from "./configs/agent-task-config-form";
import { AgentDecisionConfigForm } from "./configs/agent-decision-config-form";
import { CheckpointConfigForm } from "./configs/checkpoint-config-form";
import { McpToolCallConfigForm } from "./configs/mcp-tool-call-config-form";
import { ReferenceResolutionProvider } from "./configs/reference-resolution-provider";
import { AGENTIC_STEP_TYPES, type WorkflowStep } from "@/types/workflow.types";

interface StepConfigDrawerProps {
  step: WorkflowStep | null;
  workflowId: string;
  onClose: () => void;
}

function StepTypeChip({ stepType }: { stepType: WorkflowStep["stepType"] }) {
  const config = getStepTypeConfig(stepType);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${config.bgColor} ${config.iconColor}`}
    >
      <config.icon className="size-3" aria-hidden />
      {config.label}
    </span>
  );
}

function DrawerBody({
  step,
  workflowId,
  onClose,
}: {
  step: WorkflowStep;
  workflowId: string;
  onClose: () => void;
}) {
  const agentsEnabled = useFeatureEnabled("agents");
  if (AGENTIC_STEP_TYPES.has(step.stepType) && !agentsEnabled) {
    return <FeatureDisabledCard feature="agents" />;
  }

  switch (step.stepType) {
    case "extract":
      return <ExtractConfigForm step={step} workflowId={workflowId} onSuccess={onClose} />;
    case "load":
      return <LoadConfigForm step={step} workflowId={workflowId} onSuccess={onClose} />;
    case "llm_call":
      return <LlmCallConfigForm step={step} workflowId={workflowId} onSuccess={onClose} />;
    case "agent_task":
      return <AgentTaskConfigForm step={step} workflowId={workflowId} onSuccess={onClose} />;
    case "agent_decision":
      return <AgentDecisionConfigForm step={step} workflowId={workflowId} onSuccess={onClose} />;
    case "checkpoint":
      return <CheckpointConfigForm step={step} workflowId={workflowId} onSuccess={onClose} />;
    case "mcp_tool_call":
      return <McpToolCallConfigForm step={step} workflowId={workflowId} onSuccess={onClose} />;
    default:
      return <CatalogStepConfigForm step={step} workflowId={workflowId} onSuccess={onClose} />;
  }
}

export function StepConfigDrawer({ step, workflowId, onClose }: StepConfigDrawerProps) {
  const t = useTranslations("workflows");
  const isMobile = useIsMobile();
  const config = step ? getStepTypeConfig(step.stepType) : null;
  const StepIcon = config?.icon;

  // Mobile: fullscreen bottom sheet so the config form has maximum vertical space.
  // Desktop: 460px right-side panel (existing behaviour).
  const sheetSide = isMobile ? "bottom" : "right";
  const sheetClass = isMobile
    ? "h-[92dvh] flex flex-col rounded-t-2xl"
    : "w-full sm:w-[520px] flex flex-col";

  return (
    // key=step.id resets form state when the selected step changes,
    // preventing stale field values from bleeding across step selections.
    <Sheet
      key={step?.id ?? "closed"}
      open={!!step}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <SheetContent side={sheetSide} showCloseButton={false} className={sheetClass}>
        <SheetHeader className="flex flex-row items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            {StepIcon && config && (
              <div
                className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${config.bgColor} ${config.darkBgColor}`}
              >
                <StepIcon className={`size-4 ${config.iconColor}`} aria-hidden />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <SheetTitle className="truncate text-sm font-semibold text-[var(--foreground)]">
                {step?.stepName ?? ""}
              </SheetTitle>
              {step && (
                <div className="mt-0.5">
                  <StepTypeChip stepType={step.stepType} />
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex size-8 items-center justify-center rounded-md text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)] focus:outline-none focus:shadow-[var(--shadow-focus-ring)]"
            aria-label={t("drawer.close")}
          >
            <X className="size-4" />
          </button>
        </SheetHeader>

        <SheetBody className="flex-1 overflow-y-auto">
          {step && (
            <ReferenceResolutionProvider enabled={!!step}>
              <DrawerBody step={step} workflowId={workflowId} onClose={onClose} />
            </ReferenceResolutionProvider>
          )}
        </SheetBody>

        <SheetFooter>
          {/* Each agentic config form owns its own submit button inside SheetBody.
              Footer Close lets users dismiss without saving. */}
          <Button variant="outline" size="sm" onClick={onClose}>
            {t("drawer.close")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default StepConfigDrawer;
